import React from 'react';
import { CircleHelp, MessagesSquare, Plus } from 'lucide-react';

function formatearHora(iso) {
  if (!iso) return '';
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  const hoy = new Date();
  const esHoy = fecha.toDateString() === hoy.toDateString();
  if (esHoy) {
    return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function FilaCanal({ canal, activo, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(canal.id)}
      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors ${
        activo ? 'bg-[var(--sidebar-active)]/10' : 'hover:bg-gray-50'
      }`}
    >
      <span className="text-gray-400">#</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className={`truncate text-sm ${canal.unread > 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
            {canal.name}
          </span>
          <span className="shrink-0 text-[11px] text-gray-400">{formatearHora(canal.last_message_at)}</span>
        </span>
        {canal.last_message_preview ? (
          <span className="block truncate text-xs text-gray-400">{canal.last_message_preview}</span>
        ) : null}
      </span>
      {canal.unread > 0 && (
        <span className="ml-1 flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
          {canal.unread > 99 ? '99+' : canal.unread}
        </span>
      )}
    </button>
  );
}

/**
 * Lista de canales agrupada: primero los internos del equipo (solo los ve el
 * superadmin: venue_id NULL), después un grupo por club.
 */
export default function ChannelList({
  channels,
  activeChannelId,
  cargando,
  error,
  onSelect,
  isSuperadmin,
  vista,
  onCambiarVista,
  pendientesCount = 0,
  onNuevoCanal,
  onVerVideo,
}) {
  const internos = channels.filter((c) => !c.venue_id);
  const porVenue = new Map();
  channels
    .filter((c) => c.venue_id)
    .forEach((c) => {
      // Por venue_id, no por nombre: con dos clubes sin venue_name todos sus
      // canales colapsaban en un único grupo llamado «Club».
      const clave = String(c.venue_id);
      if (!porVenue.has(clave)) porVenue.set(clave, { nombre: c.venue_name || 'Club', canales: [] });
      porVenue.get(clave).canales.push(c);
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        {/* #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo. */}
        <MessagesSquare size={18} className="text-[#FF5400]" />
        <h1 className="text-base font-semibold text-gray-900">Smack</h1>
        <div className="ml-auto flex items-center gap-1">
          {onVerVideo ? (
            <button
              type="button"
              onClick={onVerVideo}
              className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Ver cómo funciona Smack"
              title="¿Cómo funciona?"
            >
              <CircleHelp size={18} />
            </button>
          ) : null}
        {isSuperadmin && onNuevoCanal ? (
          <button
            type="button"
            onClick={onNuevoCanal}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Nuevo canal"
            title="Nuevo canal"
          >
            <Plus size={18} />
          </button>
        ) : null}
        </div>
      </div>

      {onCambiarVista ? (
        <div className="flex items-center gap-1 border-b border-gray-200 p-2">
          <button
            type="button"
            onClick={() => onCambiarVista('canales')}
            className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-medium ${
              vista !== 'pendientes' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Canales
          </button>
          <button
            type="button"
            onClick={() => onCambiarVista('pendientes')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium ${
              vista === 'pendientes' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Pendientes
            {pendientesCount > 0 && (
              <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FF5400] px-1 text-[10px] font-bold text-white">
                {pendientesCount > 99 ? '99+' : pendientesCount}
              </span>
            )}
          </button>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {error ? <p className="px-3 py-2 text-sm text-red-600">{error}</p> : null}
        {!error && cargando ? <p className="px-3 py-2 text-sm text-gray-400">Cargando canales…</p> : null}
        {!error && !cargando && channels.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-400">
            {isSuperadmin
              ? 'No hay canales todavía. Activa Smack en un club o crea un canal interno.'
              : 'Tu club aún no tiene canales.'}
          </p>
        ) : null}

        {internos.length > 0 && (
          <div className="mb-2">
            <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Equipo
            </p>
            {internos.map((canal) => (
              <FilaCanal key={canal.id} canal={canal} activo={canal.id === activeChannelId} onSelect={onSelect} />
            ))}
          </div>
        )}

        {[...porVenue.entries()].map(([venueId, grupo]) => (
          <div key={venueId} className="mb-2">
            <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {grupo.nombre}
            </p>
            {grupo.canales.map((canal) => (
              <FilaCanal key={canal.id} canal={canal} activo={canal.id === activeChannelId} onSelect={onSelect} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
